import React, {FC, useState} from "react";
import {
  Container,
  CssBaseline,
  Avatar,
  Button,
  Link,
  Typography,
  ThemeProvider,
  Box,
  createTheme,
  Snackbar,
  Alert,
  FilledInput,
  InputLabel,
  FormControl,
  CircularProgress,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import {useNavigate} from "react-router-dom";
import {useForgotPasswordMutation} from "../../app/services/account";

const theme = createTheme();

/**
 * Self-service "forgot password" request page. Collects an email and asks the API to send a
 * reset link. The confirmation message is intentionally generic and shown regardless of whether
 * the account exists (anti-enumeration).
 */
const ForgotPasswordPage: FC = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [message, setMessage] = useState("");
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);

  const [forgotPassword, {isLoading}] = useForgotPasswordMutation();
  const navigate = useNavigate();

  const genericMessage =
    "If an account is eligible for a password reset, we've sent reset instructions to that email. Didn't get it? Your account may sign in via SSO — use your organization's login or contact your administrator.";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    await forgotPassword({email: email.trim()})
      .unwrap()
      .then(payload => {
        setMessage(payload?.message ?? genericMessage);
        setIsSubmitted(true);
      })
      .catch(() => {
        // Never reveal failure reasons here; keep the response indistinguishable.
        setMessage(genericMessage);
        setIsSubmitted(true);
      });
  }

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm">
        <CssBaseline />

        <Snackbar
          open={isSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setIsSnackbarOpen(false)}
          anchorOrigin={{vertical: "top", horizontal: "center"}}>
          <Alert severity="error" onClose={() => setIsSnackbarOpen(false)}>
            {message}
          </Alert>
        </Snackbar>

        <Box
          sx={{
            my: 16,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}>
          <Avatar sx={{m: 1, bgcolor: "secondary.main"}}>
            <LockResetIcon />
          </Avatar>
          <Typography component="h1" variant="h5">
            Forgot password
          </Typography>

          {isSubmitted ? (
            <Box sx={{mt: 3, textAlign: "center"}}>
              <Alert severity="success" sx={{mb: 2}}>
                {message}
              </Alert>
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => navigate("/login")}>
                <Typography variant="body2" color="primary">
                  Back to login
                </Typography>
              </Link>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit} sx={{mt: 1, width: "100%"}}>
              <Typography variant="body2" color="text.secondary" sx={{my: 1}}>
                Enter the email address associated with your account and we&apos;ll send you a
                link to reset your password.
              </Typography>
              <FormControl sx={{my: 1, width: "100%"}} variant="filled">
                <InputLabel htmlFor="email">Email</InputLabel>
                <FilledInput
                  name="email"
                  type="email"
                  required
                  autoFocus
                  autoComplete="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  sx={{input: {background: "white"}}}
                />
              </FormControl>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{mt: 3, mb: 2}}
                disabled={!email.trim() || isLoading}>
                Send reset link
                {isLoading && (
                  <CircularProgress
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: -12,
                      marginLeft: -12,
                    }}
                    size={24}
                  />
                )}
              </Button>

              <Box sx={{textAlign: "center"}}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => navigate("/login")}>
                  <Typography variant="body2" color="primary">
                    Back to login
                  </Typography>
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Container>
    </ThemeProvider>
  );
};

export default ForgotPasswordPage;
